import type {CSSProperties} from 'react';
import {WEDGE_TESTER_ERRORS, WEDGE_TESTER_OPS} from '@territory/tokens';
import {font, weight} from '../scale.js';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeTesterErrorsLook = 'tester-scope' | 'tester-scope-on' | 'tester-retry' | 'tester-error-row';

const TESTER_ERRORS_LOOKS = new Set<string>(['tester-scope', 'tester-scope-on', 'tester-retry', 'tester-error-row']);
export const isWedgeTesterErrorsLook = (look: string): look is WedgeTesterErrorsLook => TESTER_ERRORS_LOOKS.has(look);

/* Released Tester Tools Recent errors card (Settings, founder-testers only): three kinds of native control. The scope tab (Just me, Org-wide) is
   one of two that share a track the caller draws: a flex 1 box with 7px by 8px padding, an 8px radius, 12px type and a 200ms fade of fill and ink.
   Idle it is bare with the secondary ink and 600 weight; chosen (`tester-scope-on`) it takes the surface fill, the label ink, 700 weight and the
   fixed 1px drop (WEDGE_TESTER_ERRORS.scopeShadow). Held (the caller sets `disabled` while a read runs) it takes the wait cursor and keeps every other
   value. Retry is a block under the error text with a 10px top margin, 6px by 14px padding, an 8px radius and 12.5px semibold type on the accent
   fill with the fixed white ink (WEDGE_TESTER_OPS.accentInk). The error row is the full-width flex row button of one logged error: a 10px gap,
   top-aligned children, 11px by 13px padding, no fill and no border, left-aligned text and the Wedge font. Each reads the exact legacy roles, and
   the declaration order is the order of the source. The caller owns the labels, the glyphs, the handlers and the layout. */
const SCOPE_PADDING = '7px 8px', SCOPE_RADIUS = 8, SCOPE_FADE = 200;
const RETRY_PADDING = '6px 14px', RETRY_RADIUS = 8, RETRY_FONT = 12.5, RETRY_TOP = 10;
const ROW_PADDING = '11px 13px', ROW_GAP = 10;

export function wedgeTesterErrorsStyle(look: WedgeTesterErrorsLook, disabled = false): CSSProperties {
  const r = wedgeExactRole;
  if (look === 'tester-scope' || look === 'tester-scope-on') {
    const on = look === 'tester-scope-on';
    return {
      flex: 1, padding: SCOPE_PADDING, borderRadius: SCOPE_RADIUS,
      background: on ? r('surface') : 'transparent', color: on ? r('label') : r('labelSec'),
      border: 0, cursor: disabled ? 'wait' : 'pointer',
      fontSize: font.caption, fontWeight: on ? weight.bold : weight.semibold, fontFamily: r('font'),
      transition: `background ${SCOPE_FADE}ms ${r('ease')}, color ${SCOPE_FADE}ms ${r('ease')}`,
      boxShadow: on ? WEDGE_TESTER_ERRORS.scopeShadow : 'none',
    };
  }
  if (look === 'tester-retry') return {
    display: 'block', marginTop: RETRY_TOP, padding: RETRY_PADDING, borderRadius: RETRY_RADIUS,
    background: r('accent'), color: WEDGE_TESTER_OPS.accentInk, border: 0, cursor: 'pointer',
    fontSize: RETRY_FONT, fontWeight: weight.semibold, fontFamily: r('font'),
  };
  return {
    width: '100%', display: 'flex', alignItems: 'flex-start', gap: ROW_GAP,
    padding: ROW_PADDING,
    background: 'transparent', border: 0, textAlign: 'left',
    cursor: 'pointer', fontFamily: r('font'),
  };
}
